import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ContentPasteRoundedIcon from "@mui/icons-material/ContentPasteRounded";
import useApi from "../../../hooks/useApi";
import { hrefSeguro } from "../../../utils/hrefSeguro";
import { MAX_LINKS, linkDelPortapapeles } from "../../../utils/urlsPost";

// Correcciones que el equipo le pidió al difusor después de un reporte del
// artista (Backend/src/utils/correccionEnvio.js). Tiene 3 días para aceptar
// y entregar links nuevos, o rechazarla: entonces el envío pasa a Social y al
// artista se le devuelven sus RC. Se autoconsulta, como FeedbackVerificacion.

const fecha = (iso) =>
  new Date(iso).toLocaleString("es", {
    day: "numeric",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });

export default function Correcciones({ showSnack }) {
  const { loadApi, loadingApi } = useApi();
  const [filas, setFilas] = useState([]);
  const [entregando, setEntregando] = useState(null); // fila
  const [links, setLinks] = useState([""]);
  const [rechazando, setRechazando] = useState(null); // fila

  useEffect(() => {
    loadApi("productor/envios/correcciones", true, "get")
      .then((res) => setFilas(res.data || []))
      .catch(() => {});
  }, []);

  const actualizar = (id, cambios) =>
    setFilas((prev) =>
      prev.map((f) =>
        f.id === id ? { ...f, correccion: { ...f.correccion, ...cambios } } : f
      )
    );

  const aceptar = (fila) =>
    loadApi(`productor/envios/${fila.id}/correccion/aceptar`, true, "post", {})
      .then((res) => {
        showSnack(res.data.message);
        actualizar(fila.id, { estado: "aceptada" });
      })
      .catch((e) => showSnack(e.message, "error"));

  const abrirEntrega = (fila) => {
    setLinks([""]);
    setEntregando(fila);
  };

  const pegar = async (i) => {
    let texto;
    try {
      texto = await navigator.clipboard.readText();
    } catch (e) {
      showSnack(
        "Tu navegador no deja leer lo copiado: mantén presionado el campo y elige «Pegar».",
        "warning"
      );
      return;
    }
    const url = linkDelPortapapeles(texto);
    if (!url) {
      showSnack("Lo copiado no es un link. Copia el link de tu publicación y vuelve a intentarlo.", "warning");
      return;
    }
    setLinks((prev) => prev.map((l, j) => (j === i ? url : l)));
  };

  const entregar = () =>
    loadApi(`productor/envios/${entregando.id}/correccion/entregar`, true, "post", {
      urls_post: links.map((l) => l.trim()).filter(Boolean),
    })
      .then((res) => {
        showSnack(res.data.message);
        actualizar(entregando.id, res.data.correccion);
        setEntregando(null);
      })
      .catch((e) => showSnack(e.message, "error"));

  const rechazar = () =>
    loadApi(`productor/envios/${rechazando.id}/correccion/rechazar`, true, "post", {})
      .then((res) => {
        showSnack(res.data.message);
        setFilas((prev) => prev.filter((f) => f.id !== rechazando.id));
        setRechazando(null);
      })
      .catch((e) => showSnack(e.message, "error"));

  if (filas.length === 0) return null;

  return (
    <Box sx={{ px: { xs: 1, sm: 2 }, pt: 2 }}>
      {filas.map((fila) => {
        const c = fila.correccion;
        return (
          <Paper
            key={fila.id}
            elevation={3}
            sx={{ borderRadius: "14px", borderLeft: "6px solid #F27224", p: 2, mb: 2 }}
          >
            <Stack gap={1.5}>
              <Typography variant="h6" fontWeight={700}>
                🛠 Corrige tu envío «{fila.titulo}» de {fila.artista}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                {c.mensaje}
              </Typography>
              {c.estado === "entregada" ? (
                <Alert severity="info">
                  Entregaste la corrección: el equipo la está revisando y te
                  avisará.
                  {c.urls?.map((link) => (
                    <Box key={link} sx={{ overflowWrap: "anywhere" }}>
                      <a href={hrefSeguro(link)} target="_blank" rel="noopener noreferrer">
                        {link}
                      </a>
                    </Box>
                  ))}
                </Alert>
              ) : c.vencida ? (
                <Alert severity="warning">
                  Venció el plazo: el envío pasará a Envío Social.
                </Alert>
              ) : (
                <>
                  <Typography variant="body2" fontWeight={700}>
                    {c.estado === "aceptada"
                      ? `Aceptaste hacer el cambio. Entrega los links nuevos antes del ${fecha(c.vence)}.`
                      : `Tienes hasta el ${fecha(c.vence)}.`}
                  </Typography>
                  <Stack direction={{ xs: "column", sm: "row" }} gap={1}>
                    {c.estado === "solicitada" ? (
                      <LoadingButton
                        variant="contained"
                        loading={loadingApi.includes(
                          `post__productor/envios/${fila.id}/correccion/aceptar`
                        )}
                        onClick={() => aceptar(fila)}
                      >
                        Haré el cambio
                      </LoadingButton>
                    ) : (
                      <Button variant="contained" onClick={() => abrirEntrega(fila)}>
                        Entregar los links nuevos
                      </Button>
                    )}
                    <Button color="inherit" variant="outlined" onClick={() => setRechazando(fila)}>
                      Rechazar
                    </Button>
                  </Stack>
                </>
              )}
            </Stack>
          </Paper>
        );
      })}

      {/* Entregar los links nuevos */}
      <Dialog open={Boolean(entregando)} onClose={() => setEntregando(null)} fullWidth maxWidth="sm">
        {entregando && (
          <>
            <DialogTitle>Entregar la corrección</DialogTitle>
            <DialogContent>
              <Stack gap={2} pt={1}>
                <Typography variant="body2" color="text.secondary">
                  Pega los links de lo que publicaste para corregir el envío
                  «{entregando.titulo}»: más publicaciones en tus redes, o{" "}
                  {entregando.correccion.servicio === "Envío Editorial"
                    ? "la nota editorial"
                    : "la mención"}{" "}
                  que faltaba. Los links que ya estaban en el envío no cuentan.
                  El equipo revisará lo que entregues.
                </Typography>
                {links.map((link, i) => (
                  <Stack key={i} direction="row" gap={1} alignItems="center">
                    <TextField
                      size="small"
                      fullWidth
                      label={`Link ${i + 1}`}
                      value={link}
                      placeholder="https://"
                      inputProps={{ "aria-label": `Link ${i + 1}` }}
                      onChange={(e) =>
                        setLinks((prev) => prev.map((l, j) => (j === i ? e.target.value : l)))
                      }
                    />
                    <IconButton size="small" aria-label={`Pegar link ${i + 1}`} onClick={() => pegar(i)}>
                      <ContentPasteRoundedIcon fontSize="small" />
                    </IconButton>
                    {links.length > 1 && (
                      <IconButton
                        size="small"
                        aria-label={`Quitar link ${i + 1}`}
                        onClick={() => setLinks((prev) => prev.filter((_, j) => j !== i))}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                ))}
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  sx={{ alignSelf: "flex-start" }}
                  disabled={links.length >= MAX_LINKS}
                  onClick={() => setLinks((prev) => [...prev, ""])}
                >
                  Agregar otro link
                </Button>
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setEntregando(null)}>Cancelar</Button>
              <LoadingButton
                variant="contained"
                disabled={!links.some((l) => l.trim())}
                loading={loadingApi.includes(
                  `post__productor/envios/${entregando.id}/correccion/entregar`
                )}
                onClick={entregar}
              >
                Entregar corrección
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Rechazar la corrección */}
      <Dialog open={Boolean(rechazando)} onClose={() => setRechazando(null)} fullWidth maxWidth="sm">
        {rechazando && (
          <>
            <DialogTitle>Rechazar la corrección</DialogTitle>
            <DialogContent>
              <Alert severity="warning">
                El envío «{rechazando.titulo}» pasa a Envío Social: al artista
                se le devuelven {rechazando.correccion.devolverRC} RC
                {rechazando.correccion.descontarUSD > 0 &&
                  ` y a ti se te descuentan ${rechazando.correccion.descontarUSD} USD (cobras lo de un Envío Social)`}
                .
              </Alert>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setRechazando(null)}>Cancelar</Button>
              <LoadingButton
                variant="contained"
                color="error"
                loading={loadingApi.includes(
                  `post__productor/envios/${rechazando.id}/correccion/rechazar`
                )}
                onClick={rechazar}
              >
                Rechazar corrección
              </LoadingButton>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}
